import { Box, ButtonBase, Stack, styled, Tooltip } from '@mui/material'
import type { CSSObject } from '@mui/material/styles'
import { ArrowRightIcon, IconContext } from '@phosphor-icons/react'
import { MouseEventHandler, ReactElement, ReactNode } from 'react'
import { tokens } from 'ComponentLibrary'
import { Typography } from '../../text'

export type IconCardColor = 'royal' | 'blue' | 'turquoise' | 'green' | 'leaf' | 'lime' | 'orange' | 'red' | 'grape' | 'purple'

type IconCardVisualProps = {
    icon: ReactElement
    title: ReactNode
    text?: ReactNode
    color?: IconCardColor
}

type IconCardButtonProps = {
    variant?: 'button'
    onClick: MouseEventHandler<HTMLButtonElement>
    arrowLabel?: ReactNode
    contentChip?: ReactNode
    hideArrow?: boolean
    disabled?: boolean
    tooltip?: string
    endContent?: never
}

type IconCardCompositeProps = {
    variant: 'composite'
    endContent: ReactNode
    onClick?: never
    arrowLabel?: never
    contentChip?: never
    hideArrow?: never
    disabled?: never
    tooltip?: never
}

export type IconCardProps = IconCardVisualProps & (IconCardButtonProps | IconCardCompositeProps)

export function IconCard(props: IconCardProps) {
    const { icon, title, text, color = 'royal' } = props
    const isComposite = props.variant === 'composite'
    const disabled = isComposite ? false : props.disabled
    const content = (
        <>
            <IconContainer $color={color}>
                <IconContext.Provider
                    value={{
                        size: tokens.SizingIconM,
                        color: disabled ? tokens.ColorIconDisabled : ICON_CARD_COLORS[color].icon,
                    }}
                >
                    {icon}
                </IconContext.Provider>
            </IconContainer>
            <Stack gap={tokens.SizingSpacing075} flex={1} minWidth={0}>
                <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center" width="100%">
                    <Typography variant="headline4" color={disabled ? tokens.ColorTextDisabled : tokens.ColorTextBoldest} noWrap>
                        {title}
                    </Typography>
                    {!isComposite && (
                        <IconCardAction
                            contentChip={props.contentChip}
                            arrowLabel={props.arrowLabel}
                            hideArrow={props.hideArrow}
                            disabled={props.disabled}
                        />
                    )}
                </Stack>
                {text && (
                    <Typography variant="textXS" color={disabled ? tokens.ColorTextDisabled : tokens.ColorTextSubtle} noWrap>
                        {text}
                    </Typography>
                )}
            </Stack>
        </>
    )

    if (isComposite) {
        return (
            <StyledCompositeIconCard>
                {content}
                <IconCardEndContent>{props.endContent}</IconCardEndContent>
            </StyledCompositeIconCard>
        )
    }

    const card = (
        <StyledIconCardButton disableRipple disabled={props.disabled} onClick={props.onClick} $color={color}>
            {content}
        </StyledIconCardButton>
    )

    if (!props.tooltip) {
        return card
    }

    return (
        <Tooltip title={props.tooltip} placement="top">
            <Box component="span" width="100%">
                {card}
            </Box>
        </Tooltip>
    )
}

function IconCardAction({
    contentChip,
    arrowLabel,
    hideArrow,
    disabled,
}: Pick<IconCardButtonProps, 'contentChip' | 'arrowLabel' | 'hideArrow' | 'disabled'>) {
    if (!contentChip && hideArrow) {
        return null
    }

    return (
        <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center" flexShrink={0}>
            {contentChip}
            {!hideArrow && (
                <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100}>
                    {arrowLabel && (
                        <Typography variant="link" color={disabled ? tokens.ColorTextDisabled : tokens.ColorTextLinkDefault} noWrap>
                            {arrowLabel}
                        </Typography>
                    )}
                    <ArrowRightIcon
                        size={tokens.SizingIconS}
                        color={disabled ? tokens.ColorIconDisabled : tokens.ColorIconBoldest}
                        weight="bold"
                    />
                </Stack>
            )}
        </Stack>
    )
}

const iconCardStyles = {
    width: '100%',
    minHeight: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.SizingSpacing200,
    // Figma's 12px inset includes the 1px border, so border-box needs 11px padding.
    padding: `calc(${tokens.SizingSpacing150} - ${tokens.SizingBorderS})`,
    overflow: 'hidden',
    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    borderRadius: tokens.SizingCornerM,
    backgroundColor: tokens.ColorSurfaceDefault,
    boxSizing: 'border-box',
    textAlign: 'left',
} satisfies CSSObject

const StyledIconCardButton = styled(ButtonBase, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $color: IconCardColor }>(({ $color }) => ({
    ...iconCardStyles,
    '&:not(.Mui-disabled)': {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: ICON_CARD_COLORS[$color].surface,
        },
        '&:active': {
            backgroundColor: ICON_CARD_COLORS[$color].surface,
        },
    },
    '&.Mui-disabled': {
        opacity: 1,
        cursor: 'default',
    },
}))

const StyledCompositeIconCard = styled('div')(() => iconCardStyles)

const IconCardEndContent = styled('div')({
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
})

const IconContainer = styled('span', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $color: IconCardColor }>(({ $color }) => ({
    width: '40px',
    height: '56px',
    display: 'inline-flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.SizingCornerS,
    color: ICON_CARD_COLORS[$color].icon,
    backgroundColor: ICON_CARD_COLORS[$color].surface,
}))

const ICON_CARD_COLORS: Record<IconCardColor, { surface: string; icon: string }> = {
    royal: {
        surface: tokens.ColorSurfaceAccentRoyalDefault,
        icon: tokens.ColorIconAccentRoyal,
    },
    blue: {
        surface: tokens.ColorSurfaceAccentBlueDefault,
        icon: tokens.ColorIconAccentBlue,
    },
    turquoise: {
        surface: tokens.ColorSurfaceAccentTurquoiseDefault,
        icon: tokens.ColorIconAccentTurquoise,
    },
    green: {
        surface: tokens.ColorSurfaceAccentGreenDefault,
        icon: tokens.ColorIconAccentGreen,
    },
    leaf: {
        surface: tokens.ColorSurfaceAccentLeafDefault,
        icon: tokens.ColorIconAccentLeaf,
    },
    lime: {
        surface: tokens.ColorSurfaceAccentLimeDefault,
        icon: tokens.ColorIconAccentLime,
    },
    orange: {
        surface: tokens.ColorSurfaceAccentOrangeDefault,
        icon: tokens.ColorIconAccentOrange,
    },
    red: {
        surface: tokens.ColorSurfaceAccentRedDefault,
        icon: tokens.ColorIconAccentRed,
    },
    grape: {
        surface: tokens.ColorSurfaceAccentGrapeDefault,
        icon: tokens.ColorIconAccentGrape,
    },
    purple: {
        surface: tokens.ColorSurfaceAccentPurpleDefault,
        icon: tokens.ColorIconAccentPurple,
    },
}
